import { useMemo } from 'react';
import { useModuleConfig } from './queries/admin/useModuleConfig';
import type { IemModuleConfig } from '../types/moduleConfig';

/** Shape of the IE Kit label bag. */
export interface IeKitLabels {
  /** General singular label for the screen, e.g. "IE Kit" */
  label: string;
  /** General plural label for the screen, e.g. "IE Kits" */
  labelPlural: string;
  /** Tab-specific label (Front Window tab), falls back to label */
  tabLabel: string;
  /** Column-header-specific label, falls back to label */
  columnLabel: string;
}

/** Screens that can have independent IE Kit label overrides. */
export type IeKitScreen = 'front_window' | 'reports' | 'global_search';

const FALLBACK: IeKitLabels = {
  label: 'IE Kit',
  labelPlural: 'IE Kits',
  tabLabel: 'IE Kit',
  columnLabel: 'IE Kit',
};

/**
 * Provides the configurable IE Kit labels from module configuration.
 * Pass a screen name to get the per-screen override; falls back to the
 * global ie_kit_label / ie_kit_label_plural if the per-screen value is empty.
 */
export function useIeKitLabels(screen?: IeKitScreen): IeKitLabels {
  const { config } = useModuleConfig<IemModuleConfig>('iem');
  const ieKits = config.features.ie_kits;

  return useMemo<IeKitLabels>(() => {
    const globalLabel = ieKits?.ie_kit_label || FALLBACK.label;
    const globalPlural = ieKits?.ie_kit_label_plural || FALLBACK.labelPlural;

    if (!screen)
      return {
        label: globalLabel,
        labelPlural: globalPlural,
        tabLabel: globalLabel,
        columnLabel: globalLabel,
      };

    // Per-screen specific labels, falling back to global ie_kit_label
    const screenLabel = ieKits?.[`${screen}_label` as keyof typeof ieKits] as string | undefined;
    const screenPlural = ieKits?.[`${screen}_label_plural` as keyof typeof ieKits] as
      | string
      | undefined;

    // Per-element overrides (front_window has tab + column; others use screen label)
    const tabLabel =
      (screen === 'front_window' ? ieKits?.front_window_tab_label : undefined) ||
      screenLabel ||
      globalLabel;
    const columnLabel =
      (screen === 'front_window' ? ieKits?.front_window_column_label : undefined) ||
      screenLabel ||
      globalLabel;

    return {
      label: screenLabel || globalLabel,
      labelPlural: screenPlural || globalPlural,
      tabLabel,
      columnLabel,
    };
  }, [ieKits, screen]);
}

/**
 * Returns the configured IE Kit identifier prefix (e.g. "BOI" for USASOC, "IE" default).
 * Use `formatIeKitIdentifier` to replace the stored "IE-" prefix with the configured one for display.
 */
export function useIeKitIdPrefix(): {
  prefix: string;
  formatIdentifier: (identifier: string | null | undefined) => string;
} {
  const { config } = useModuleConfig<IemModuleConfig>('iem');
  const prefix = config.features.ie_kits?.ie_kit_id_prefix || 'IE';

  return useMemo(
    () => ({
      prefix,
      formatIdentifier: (identifier: string | null | undefined): string => {
        if (!identifier) return '';
        if (prefix === 'IE') return identifier;
        return identifier.replace(/^IE-/, `${prefix}-`);
      },
    }),
    [prefix],
  );
}

/** Returns the configured label for the restricted-issue tag (e.g. "SOC" for USASOC). */
export function useRestrictedTagLabel(): string {
  const { config } = useModuleConfig<IemModuleConfig>('iem');
  return config?.features?.ie_kits?.restricted_tag_label || 'Restricted';
}
